import React, {useEffect, useMemo, useState} from "react";
import {withRouter} from "react-router-dom";
import {styled} from "@mui/styles";
import {
    AppBar,
    BottomNavigation,
    BottomNavigationAction,
    Divider,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    Stack,
    Typography
} from "@mui/material";
import {
    Attachment as FilesIcon,
    Fingerprint as PasswordIcon,
    Folder as CoursesIcon,
    Help as HelpIcon,
    Info as AboutIcon,
    Logout as LogoutIcon,
    MoreHoriz as MoreIcon,
    Notifications as NotificationsIcon,
    People as UsersIcon,
    PeopleOutline as ContactsIcon,
    Quiz as QuestionnaireIcon,
    Settings as SettingsIcon,
    Wysiwyg as TemplateIcon,
    YouTube as YouTubeIcon,
} from "@mui/icons-material";
import {MoreMenu} from "../nav";
import {confirm, mobile, router} from "@atttomyx/react-utils";
import {arrays, strings} from "@atttomyx/shared-utils";
import {
    PAGE_ABOUT,
    PAGE_CONTACTS,
    PAGE_COURSE,
    PAGE_COURSES,
    PAGE_FILES,
    PAGE_NOTIFICATIONS,
    PAGE_PASSWORD,
    PAGE_QUESTIONNAIRE,
    PAGE_QUESTIONNAIRES,
    PAGE_SETTINGS,
    PAGE_TEMPLATE,
    PAGE_TEMPLATES,
    PAGE_USER,
    PAGE_USERS,
    PAGES_MAIN,
    PAGES_MORE,
} from "../../constants";
import "./footer.css";

const ActiveCoursesIcon = styled(CoursesIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveCoursesIcon = styled(CoursesIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const ActiveQuestionnaireIcon = styled(QuestionnaireIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveQuestionnaireIcon = styled(QuestionnaireIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const ActiveTemplateIcon = styled(TemplateIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveTemplateIcon = styled(TemplateIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const ActiveFilesIcon = styled(FilesIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveFilesIcon = styled(FilesIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const ActiveUsersIcon = styled(UsersIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveUsersIcon = styled(UsersIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const ActiveContactsIcon = styled(ContactsIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveContactsIcon = styled(ContactsIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const ActiveNotificationsIcon = styled(NotificationsIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveNotificationsIcon = styled(NotificationsIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));
const AlertNotificationsIcon = styled(NotificationsIcon)(({ theme }) => {
    return ({
        color: theme.palette.error.main,
    })
});

const ActiveMoreIcon = styled(MoreIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveMoreIcon = styled(MoreIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));
const AlertMoreIcon = styled(MoreIcon)(({ theme }) => ({
    color: theme.palette.error.main,
}));

const ActiveSettingsIcon = styled(SettingsIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveSettingsIcon = styled(SettingsIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const ActivePasswordIcon = styled(PasswordIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactivePasswordIcon = styled(PasswordIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const ActiveAboutIcon = styled(AboutIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveAboutIcon = styled(AboutIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const InactiveLogoutIcon = styled(LogoutIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));
const InactiveHelpIcon = styled(HelpIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const ActiveYouTubeIcon = styled(YouTubeIcon)(({ theme }) => ({
    color: theme.palette.primary.main,
}));
const InactiveYouTubeIcon = styled(YouTubeIcon)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const PAGE_MORE = "more";
const PAGE_OTHER = "other";

const NavButton = (props) => {
    const { label, active, icon, onClick } = props;

    return (
        <Stack direction="column" onClick={onClick} className="nav-button">
            {icon}
            <Typography variant="caption" color={active ? "primary" : "text.secondary"}>
                {label}
            </Typography>
        </Stack>
    )
};

const Footer = (props) => {
    const { dimensions, history, user, onLogout, onHelp, numNotifications } = props;
    const [showMore, setShowMore] = useState(false);
    const path = strings.sanitizeStr(router.getCurrentPage(history));

    const page = useMemo(() => {
        let ret = PAGE_OTHER;

        if (path === PAGE_USER || path.startsWith(PAGE_USER + "/")) {
            ret = PAGE_USERS;

        } else if (path === PAGE_COURSE || path.startsWith(PAGE_COURSE + "/")) {
            ret = PAGE_COURSES;

        } else if (path === PAGE_QUESTIONNAIRE || path.startsWith(PAGE_QUESTIONNAIRE + "/")) {
            ret = PAGE_QUESTIONNAIRES;

        } else if (path === PAGE_TEMPLATE || path.startsWith(PAGE_TEMPLATE + "/")) {
            ret = PAGE_TEMPLATES;

        } else if (arrays.contains(PAGES_MAIN, path)) {
            ret = path;

        } else if (arrays.contains(PAGES_MORE, path)) {
            ret = dimensions.portrait || dimensions.short ? PAGE_MORE : path;
        }

        return ret;
    }, [dimensions, path]);

    useEffect(() => {
        dimensions.onResize();
    }, []);

    const renderBottom = () => {
        return (
            <AppBar className="bottom-nav" position="fixed" color="default">
                <BottomNavigation
                    value={page}
                    showLabels={true}
                >
                    {user.roles.admin || user.roles.inventor ?
                        <BottomNavigationAction
                            value={PAGE_COURSES}
                            label="Modules"
                            icon={<CoursesIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_COURSES)}
                        /> : null}
                    {user.roles.admin || user.roles.inventor ?
                        <BottomNavigationAction
                            value={PAGE_QUESTIONNAIRES}
                            label="Forms"
                            icon={<QuestionnaireIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_QUESTIONNAIRES)}
                        /> : null}
                    {user.roles.inventor ?
                        <BottomNavigationAction
                            value={PAGE_FILES}
                            label="Files"
                            icon={<FilesIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_FILES)}
                        /> : null}
                    {user.roles.inventor ?
                        <BottomNavigationAction
                            label="YouTube"
                            icon={<YouTubeIcon/>}
                            onClick={() => window.open("https://www.youtube.com/@inventorsarena?sub_confirmation=1", "_blank")}
                        /> : null}
                    {user.roles.admin || user.roles.investor ?
                        <BottomNavigationAction
                            value={PAGE_USERS}
                            label={user.roles.admin ? "Users" : "Inventors"}
                            icon={<UsersIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_USERS)}
                        /> : null}
                    {user.roles.admin ?
                        <BottomNavigationAction
                            value={PAGE_CONTACTS}
                            label="Contacts"
                            icon={<ContactsIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_CONTACTS)}
                        /> : null}
                    <BottomNavigationAction
                        value={PAGE_MORE}
                        label="More"
                        icon={<MoreIcon color={numNotifications > 0 ? "error" : undefined}/>}
                        component="div"
                        onClick={() => setShowMore(true)}
                    />
                </BottomNavigation>
            </AppBar>
        )
    };

    const renderLeftMobile = () => {
        return (
            <Drawer
                variant="permanent"
                anchor="left"
                open={false}
                className="left-nav"
            >
                <div className="nav-buttons">
                    {user.roles.admin || user.roles.inventor ?
                        <NavButton
                            label="Modules"
                            active={page === PAGE_COURSES}
                            icon={page === PAGE_COURSES ? <ActiveCoursesIcon/> : <InactiveCoursesIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_COURSES)}
                        /> : null}
                    {user.roles.admin || user.roles.inventor ?
                        <NavButton
                            label="Forms"
                            active={page === PAGE_QUESTIONNAIRES}
                            icon={page === PAGE_QUESTIONNAIRES ? <ActiveQuestionnaireIcon/> : <InactiveQuestionnaireIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_QUESTIONNAIRES)}
                        /> : null}
                    {user.roles.inventor ?
                        <NavButton
                            label="Files"
                            active={page === PAGE_FILES}
                            icon={page === PAGE_FILES ? <ActiveFilesIcon/> : <InactiveFilesIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_FILES)}
                        /> : null}
                    {user.roles.admin || user.roles.investor ?
                        <NavButton
                            label={user.roles.admin ? "Users" : "Inventors"}
                            active={page === PAGE_USERS}
                            icon={page === PAGE_USERS ? <ActiveUsersIcon/> : <InactiveUsersIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_USERS)}
                        /> : null}
                    {user.roles.admin ?
                        <NavButton
                            label="Contacts"
                            active={page === PAGE_CONTACTS}
                            icon={page === PAGE_CONTACTS ? <ActiveContactsIcon/> : <InactiveContactsIcon/>}
                            onClick={() => router.redirectTo(history, PAGE_USERS)}
                        /> : null}
                    {user.roles.inventor ?
                        <NavButton
                            label="YouTube"
                            active={false}
                            icon={<InactiveYouTubeIcon/>}
                            onClick={() => window.open("https://www.youtube.com/@inventorsarena?sub_confirmation=1", "_blank")}
                        /> : null}
                    <NavButton
                        label="More"
                        active={page === PAGE_MORE}
                        icon={page === PAGE_MORE ? <ActiveMoreIcon/> : numNotifications > 0 ? <AlertMoreIcon/> : <InactiveMoreIcon/>}
                        onClick={() => setShowMore(true)}
                    />
                </div>
            </Drawer>
        )
    };

    const renderLeftDesktop = () => {
        return (
            <Drawer
                variant="permanent"
                anchor="left"
                open={false}
                className="left-nav"
            >
                <List>
                    {user.roles.admin || user.roles.inventor ?
                        <ListItemButton onClick={() => router.redirectTo(history, PAGE_COURSES)}>
                            <ListItemIcon title="Modules">
                                {page === PAGE_COURSES ? <ActiveCoursesIcon/> : <InactiveCoursesIcon/>}
                            </ListItemIcon>
                        </ListItemButton> : null}
                    {user.roles.admin || user.roles.inventor ?
                        <ListItemButton onClick={() => router.redirectTo(history, PAGE_QUESTIONNAIRES)}>
                            <ListItemIcon title="Forms">
                                {page === PAGE_QUESTIONNAIRES ? <ActiveQuestionnaireIcon/> : <InactiveQuestionnaireIcon/>}
                            </ListItemIcon>
                        </ListItemButton> : null}
                    {user.roles.admin ?
                        <ListItemButton onClick={() => router.redirectTo(history, PAGE_TEMPLATES)}>
                            <ListItemIcon title="Templates">
                                {page === PAGE_TEMPLATES ? <ActiveTemplateIcon/> : <InactiveTemplateIcon/>}
                            </ListItemIcon>
                        </ListItemButton> : null}
                    {user.roles.inventor ?
                        <ListItemButton onClick={() => router.redirectTo(history, PAGE_FILES)}>
                            <ListItemIcon title="Files">
                                {page === PAGE_FILES ? <ActiveFilesIcon/> : <InactiveFilesIcon/>}
                            </ListItemIcon>
                        </ListItemButton> : null}
                    {user.roles.admin || user.roles.investor ?
                        <ListItemButton onClick={() => router.redirectTo(history, PAGE_USERS)}>
                            <ListItemIcon title={user.roles.admin ? "Users" : "Inventors"}>
                                {page === PAGE_USERS ? <ActiveUsersIcon/> : <InactiveUsersIcon/>}
                            </ListItemIcon>
                        </ListItemButton> : null}
                    {user.roles.admin ?
                        <ListItemButton onClick={() => router.redirectTo(history, PAGE_CONTACTS)}>
                            <ListItemIcon title="Contacts">
                                {page === PAGE_CONTACTS ? <ActiveContactsIcon/> : <InactiveContactsIcon/>}
                            </ListItemIcon>
                        </ListItemButton> : null}
                    <ListItemButton onClick={() => router.redirectTo(history, PAGE_NOTIFICATIONS)}>
                        <ListItemIcon title="Notifications">
                            {page === PAGE_NOTIFICATIONS
                                ? <ActiveNotificationsIcon/>
                                : numNotifications > 0 ? <AlertNotificationsIcon/>
                                    : <InactiveNotificationsIcon/>}
                        </ListItemIcon>
                    </ListItemButton>
                    <Divider/>
                    <ListItemButton onClick={() => router.redirectTo(history, PAGE_SETTINGS)}>
                        <ListItemIcon title="Settings">
                            {page === PAGE_SETTINGS ? <ActiveSettingsIcon/> : <InactiveSettingsIcon/>}
                        </ListItemIcon>
                    </ListItemButton>
                    <ListItemButton onClick={() => router.redirectTo(history, PAGE_PASSWORD)}>
                        <ListItemIcon title="Password">
                            {page === PAGE_PASSWORD ? <ActivePasswordIcon/> : <InactivePasswordIcon/>}
                        </ListItemIcon>
                    </ListItemButton>
                    <ListItemButton onClick={() => confirm.confirm(mobile.isMobile()
                        ? "Someone would need to have physical access to your phone to access this account. Are you sure you want to logout?"
                        : "Are you sure you want to logout?", onLogout)}>
                        <ListItemIcon title="Logout">
                            <InactiveLogoutIcon/>
                        </ListItemIcon>
                    </ListItemButton>
                    <Divider/>
                    <ListItemButton onClick={() => router.redirectTo(history, PAGE_ABOUT)}>
                        <ListItemIcon title="About">
                            {page === PAGE_ABOUT ? <ActiveAboutIcon/> : <InactiveAboutIcon/>}
                        </ListItemIcon>
                    </ListItemButton>
                    <ListItemButton onClick={onHelp}>
                        <ListItemIcon title="Help">
                            <InactiveHelpIcon/>
                        </ListItemIcon>
                    </ListItemButton>
                    <Divider/>
                    <ListItemButton onClick={() => window.open("https://www.youtube.com/@inventorsarena?sub_confirmation=1", "_blank")}>
                        <ListItemIcon title="YouTube">
                            <InactiveYouTubeIcon/>
                        </ListItemIcon>
                    </ListItemButton>
                </List>
            </Drawer>
        )
    };

    return <div className="footer">
        {dimensions.landscape ? dimensions.short ? renderLeftMobile() : renderLeftDesktop() : renderBottom()}
        {showMore ?
            <MoreMenu
                user={user}
                onCancel={() => setShowMore(false)}
                onLogout={onLogout}
                onHelp={onHelp}
                numNotifications={numNotifications}
            /> : null}
    </div>
}

export default withRouter(Footer);
